import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { webApiUrl } from '@domains/shared/domain/utils/url';
import { headers } from '@domains/shared/domain/utils/global-constants';
import { StudentCourseArticleDTO } from '../../models/student/student-course-article.interface';
import { StudentCourseArticleCreateFeedbackDTO } from '../../models/student/student-course-article-create-feedback.interface';
import { StudentCourseArticleCreateAnswerDTO } from '../../models/student/student-course-article-create-answer.interface';
import { QuizEvaluationDTO } from '../../models/student/quiz/quiz-evaluation.interface';
import {
  DismissFeedbackDTO,
  FeedbackPromptDTO,
  SubmitFeedbackDTO,
} from '../../models/student/feedback-prompt.interface';


@Injectable()
export class StudentCompanyCourseArticleService {

  private baseUrl =  `${webApiUrl}/student/Article`;

  constructor(private http: HttpClient) {}

  getArticle(articleId: string): Observable<StudentCourseArticleDTO> {
    const url = [this.baseUrl, articleId].join('/');
    return this.http.get<StudentCourseArticleDTO>(url, { headers });
  }

  createFeedback(articleId: string, data: StudentCourseArticleCreateFeedbackDTO): Observable<void> {
    const url = [this.baseUrl, articleId, 'feedback'].join('/');
    return this.http.post<void>(url, data, { headers });
  }

  /**
   * Dotaz na hodnocení, nebo nic.
   *
   * ⚠ Server vrací 204 s prázdným tělem, když se na nic neptáme — `HttpClient` z toho
   * udělá `null`. Není to chyba: dotaz je vedlejší věc u článku a 404 by v konzoli
   * vypadalo jako rozbitá stránka.
   */
  getFeedbackPrompt(articleMetaId: string): Observable<FeedbackPromptDTO | null> {
    const url = [this.baseUrl, articleMetaId, 'feedback-prompt'].join('/');
    return this.http.get<FeedbackPromptDTO | null>(url, { headers });
  }

  submitFeedbackPrompt(articleMetaId: string, data: SubmitFeedbackDTO): Observable<void> {
    const url = [this.baseUrl, articleMetaId, 'feedback-prompt'].join('/');
    return this.http.post<void>(url, data, { headers });
  }

  dismissFeedbackPrompt(articleMetaId: string, data: DismissFeedbackDTO): Observable<void> {
    const url = [this.baseUrl, articleMetaId, 'feedback-prompt', 'dismiss'].join('/');
    return this.http.post<void>(url, data, { headers });
  }

  createStatistics(articleId: string): Observable<void> {
    const url = [this.baseUrl, articleId, 'statistics'].join('/');
    return this.http.post<void>(url, {}, { headers });
  }

  /** Změřený čas čtení kapitoly; strop na jeden ping hlídá server. */
  addReadingTime(articleId: string, seconds: number): Observable<void> {
    const url = [this.baseUrl, articleId, 'ReadingTime'].join('/');
    return this.http.post<void>(url, { seconds }, { headers });
  }

  /**
   * Vyhodnotí odpověď na otázku kvízu na serveru. Vrací, které varianty jsou správné —
   * běhový tvar otázky to nenese, takže je to jediná cesta, jak se to dozvědět.
   */
  evaluateQuiz(articleMetaId: string, questionId: string, selectedAnswerIds: string[]): Observable<QuizEvaluationDTO> {
    const url = [this.baseUrl, articleMetaId, 'quiz', questionId, 'evaluate'].join('/');

    return this.http.post<QuizEvaluationDTO>(url, selectedAnswerIds, { headers });
  }

  createAnswers(articleId: string, data: StudentCourseArticleCreateAnswerDTO[]): Observable<void> {
    const url = [this.baseUrl, articleId, 'answers'].join('/');
    return this.http.post<void>(url, data, { headers });
  }

  clearAnswers(articleId: string): Observable<void> {
    const url = [this.baseUrl, articleId, 'answers'].join('/');
    return this.http.delete<void>(url, { headers });
  }

  registerSectionExpanded(articleSectionId: string): Observable<void> {
    const url = [`${webApiUrl}/student/ArticleSection`, articleSectionId].join('/');
    return this.http.post<void>(url, {}, { headers });
  }
}
